// src/hooks/useFinancialData.js
import { useState, useEffect } from "react";
import { db } from "../lib/firebase";
import {
  collection,
  doc,
  onSnapshot,
  query,
  orderBy,
  limit,
  where,
  Timestamp,
} from "firebase/firestore";

const USER_ID = "gavin"; // Single user app

export function useAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = collection(db, "users", USER_ID, "accounts");
    return onSnapshot(q, (snap) => {
      setAccounts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, []);

  return { accounts, loading };
}

export function useTransactions(filters = {}) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = query(
      collection(db, "users", USER_ID, "transactions"),
      orderBy("date", "desc"),
      limit(filters.limit || 100)
    );

    if (filters.accountId) {
      q = query(q, where("accountId", "==", filters.accountId));
    }
    if (filters.category) {
      q = query(q, where("category", "==", filters.category));
    }
    if (filters.reviewOnly) {
      q = query(q, where("needsReview", "==", true));
    }
    if (filters.startDate) {
      q = query(q, where("date", ">=", Timestamp.fromDate(filters.startDate)));
    }

    return onSnapshot(q, (snap) => {
      setTransactions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, [filters.accountId, filters.category, filters.reviewOnly]);

  return { transactions, loading };
}

export function useNetWorthHistory(range = "1M") {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const rangeMap = { "1M": 30, "3M": 90, "6M": 180, "1Y": 365, All: 9999 };

  useEffect(() => {
    const days = rangeMap[range] || 30;
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const q = query(
      collection(db, "users", USER_ID, "netWorthHistory"),
      where("date", ">=", Timestamp.fromDate(startDate)),
      orderBy("date", "asc")
    );

    return onSnapshot(q, (snap) => {
      setHistory(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, [range]);

  return { history, loading };
}

export function useLoans() {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = collection(db, "users", USER_ID, "loans");
    return onSnapshot(q, (snap) => {
      setLoans(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, []);

  return { loans, loading };
}

export function useInvestments() {
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = collection(db, "users", USER_ID, "holdings");
    return onSnapshot(q, (snap) => {
      setHoldings(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, []);

  return { holdings, loading };
}

export function useCashBalance() {
  const [cash, setCash] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onSnapshot(doc(db, "users", USER_ID, "manual", "cash"), (snap) => {
      if (snap.exists()) setCash(snap.data());
      setLoading(false);
    });
  }, []);

  return { cash, loading };
}

export function useAIConversations() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, "users", USER_ID, "aiConversations"),
      orderBy("updatedAt", "desc"),
      limit(20)
    );
    return onSnapshot(q, (snap) => {
      setConversations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, []);

  return { conversations, loading };
}

export function useSpendingByCategory(month, year) {
  const [spending, setSpending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);

    const q = query(
      collection(db, "users", USER_ID, "transactions"),
      where("date", ">=", Timestamp.fromDate(startDate)),
      where("date", "<=", Timestamp.fromDate(endDate)),
      where("amount", "<", 0)
    );

    return onSnapshot(q, (snap) => {
      const txs = snap.docs.map((d) => d.data());
      const byCategory = {};
      txs.forEach((tx) => {
        const cat = tx.category || "Uncategorized";
        if (!byCategory[cat]) byCategory[cat] = { category: cat, total: 0, count: 0 };
        byCategory[cat].total += Math.abs(tx.amount);
        byCategory[cat].count += 1;
      });
      setSpending(Object.values(byCategory).sort((a, b) => b.total - a.total));
      setLoading(false);
    });
  }, [month, year]);

  return { spending, loading };
}
